import { UserConfig } from '@/stores/useConfigStore'

const CONFIG_STORAGE_KEY = 'userConfig'

const defaultConfig: UserConfig = {
  layout: "grid",
  activeView: "task",
  showCalendarSidebar: false,
  plannerViewCalendarView: "dayGridMonth",
  accounts: {},
  tasklists: {},
  seenFeatures: {}
}

export const configService = {
  getFromLocalStorage(): UserConfig | null {
    if (typeof window === 'undefined') return null
    
    try {
      const stored = window.localStorage.getItem(CONFIG_STORAGE_KEY)
      if (!stored) return null
      return JSON.parse(stored) as UserConfig
    } catch (error) {
      console.warn('Error reading config from localStorage:', error)
      return null
    }
  },

  saveToLocalStorage(config: UserConfig): void {
    if (typeof window === 'undefined') return
    
    try {
      window.localStorage.setItem(CONFIG_STORAGE_KEY, JSON.stringify(config))
    } catch (error) {
      console.warn('Error saving config to localStorage:', error)
    }
  },

  async getFromFirestore(): Promise<UserConfig | null> {
    try {
      const { getUserConfig } = await import('@/api/api')
      return await getUserConfig() as UserConfig
    } catch (error) {
      console.warn('Error fetching config from Firestore:', error)
      return null
    }
  },

  async saveToFirestore(config: UserConfig): Promise<void> {
    try {
      const { updateUserConfig } = await import('@/api/api')
      await updateUserConfig(config)
    } catch (error) {
      console.warn('Error saving config to Firestore:', error)
      throw error
    }
  },

  mergeConfigs(localConfig: UserConfig | null, firestoreConfig: UserConfig | null): UserConfig {
    const base = { ...defaultConfig }
    
    if (firestoreConfig) {
      base.layout = firestoreConfig.layout || base.layout
      base.activeView = firestoreConfig.activeView || base.activeView
      base.showCalendarSidebar = firestoreConfig.showCalendarSidebar ?? base.showCalendarSidebar
      base.plannerViewCalendarView = firestoreConfig.plannerViewCalendarView || base.plannerViewCalendarView
      base.accounts = { ...base.accounts, ...firestoreConfig.accounts }
      base.tasklists = { ...base.tasklists, ...firestoreConfig.tasklists }
      base.seenFeatures = { ...base.seenFeatures, ...firestoreConfig.seenFeatures }
    }
    
    if (localConfig) {
      base.layout = localConfig.layout || base.layout
      base.activeView = localConfig.activeView || base.activeView
      base.showCalendarSidebar = localConfig.showCalendarSidebar ?? base.showCalendarSidebar
      base.plannerViewCalendarView = localConfig.plannerViewCalendarView || base.plannerViewCalendarView
      base.accounts = { ...base.accounts, ...localConfig.accounts }
      
      Object.keys(localConfig.tasklists || {}).forEach(accountId => {
        base.tasklists[accountId] = {
          ...base.tasklists[accountId],
          ...localConfig.tasklists[accountId]
        }
      })
      
      base.seenFeatures = { ...base.seenFeatures, ...localConfig.seenFeatures }
    }
    
    return base
  },

  validateConfig(config: UserConfig, accountIds: string[], tasklistIdsByAccount?: Record<string, string[]>): UserConfig {
    const validated = { ...config }
    
    Object.keys(validated.accounts).forEach(accountId => {
      if (!accountIds.includes(accountId)) {
        delete validated.accounts[accountId]
      }
    })
    
    Object.keys(validated.tasklists).forEach(accountId => {
      if (!accountIds.includes(accountId)) {
        delete validated.tasklists[accountId]
      }
    })
    
    // Validate listOrder entries if tasklistIds are provided
    if (tasklistIdsByAccount) {
      Object.keys(validated.accounts).forEach(accountId => {
        if (validated.accounts[accountId]?.listOrder) {
          const validTasklistIds = tasklistIdsByAccount[accountId] || []
          validated.accounts[accountId] = {
            ...validated.accounts[accountId],
            listOrder: validated.accounts[accountId].listOrder.filter(id => validTasklistIds.includes(id))
          }
        }
      })
      
      // Also clean up tasklist configs for non-existent tasklists
      Object.keys(validated.tasklists).forEach(accountId => {
        const validTasklistIds = tasklistIdsByAccount[accountId] || []
        Object.keys(validated.tasklists[accountId] || {}).forEach(tasklistId => {
          if (!validTasklistIds.includes(tasklistId)) {
            delete validated.tasklists[accountId][tasklistId]
          }
        })
        if (Object.keys(validated.tasklists[accountId] || {}).length === 0) {
          delete validated.tasklists[accountId]
        }
      })
    }
    
    return validated
  }
}

